/******************************************************************************
 * 
 * Strauss Motion
 * 
 * Copyright (c) 2022 Robimo GmbH
 *
 * Authors: Daniel Martinek
 *
 *****************************************************************************/
import { Fragment} from 'react';
import { useTranslation } from 'react-i18next';

import {ReactComponent as IconArrowDown} from '../icons/arrow_down.svg';
import { ConfigState, CountryData, LibraryData, LogoType, PricingData, ProductData, ProductState } from '../store/interfaces';
import { translate } from '../i18n';

import {isMobile} from 'react-device-detect';

import {ReactComponent as IconPlus} from '../icons/plus.svg';
import {ReactComponent as IconMinus} from '../icons/minus.svg';
import {ReactComponent as IconTrash} from '../icons/remove.svg';
import {ReactComponent as IconShirt} from '../icons/logo.svg';

import { multiplyPrice, getPricingText, getPricingTextLong, applyVat } from '../helpers/pricing';

import styles from '../styles/product.module.scss'
import { getURL, GET_LOGO } from '../store/api';
import { getMinimumOrderAmount, getTotalProductOrderAmountForMinimumOrder } from '../helpers/helpers';

interface ProductProps {
    index: number;
    library: LibraryData;
    product: ProductState;
    article: ProductData;
    config: ConfigState;
    country: CountryData;
    vat: number;
    protected?: boolean;
    noSeparationLines?: boolean;    
    divRef: React.RefObject<HTMLDivElement>;
    onProductUpdate?: (index: number, state: ProductState) => void;
    onProductRemove?: (index: number) => void;
    onUiGoTo?: (route: string) => void;
    onUiSetPreviewArticle1?: (article: string) => void;
    onUiSetEditScreenTab?: (editScreenTab: string) => void;
    onUiSetDetailArticleIndex?: (detailArticleIndex: number) => void;
}

export function Product(props:ProductProps) {
    const {t, i18n} = useTranslation();

    let basePrice = props.article.pricing[0].pricing;
    let pricingNoVat = getPricingText(i18n.language, props.country, basePrice);
    let pricingVat = getPricingText(i18n.language, props.country, applyVat(basePrice, props.vat));

    let activePrice: PricingData | null = null;
    let overallAmount = 0;
    props.product.order.forEach(order => {
        overallAmount += order.amount;
    });

    for (let index = props.article.pricing.length - 1; index >= 0; index--) {
        const element = props.article.pricing[index];
        if(overallAmount >= element.minAmount) {
            activePrice = element.pricing;
            break;
        }
    }

    if(activePrice === null) {
        return null;
    }

    let overallPriceNoVat = multiplyPrice(activePrice, overallAmount);
    let overallPriceVat = applyVat(overallPriceNoVat, props.vat);

    const updatedProduct = structuredClone(props.product);

    return <Fragment>
        <div className={`${styles.product} ${props.noSeparationLines ? styles.productNoSeparation : ''}`} key={props.index}>
            {!isMobile && <div className={styles.productPreview} ref={props.divRef}>   
            </div>}
            <div className={styles.productInfoContainer}>
                <div className={styles.productInfo}>
                    {isMobile && <div className={styles.productPreview} ref={props.divRef}>                     
                    </div>}
                    <div>
                        <h1>{translate(props.article.name, i18n)}</h1>
                        {isMobile && !props.protected &&
                            <button data-testid="mymotion_orderListRemoveProduct" className={`${styles.buttonWhite} ${styles.buttonRemove}`} onClick={() => {
                                if(props.onProductRemove) {
                                    props.onProductRemove(props.index);
                                }
                            }} ><IconTrash /></button>
                        }
                        <p className={styles.priceWithoutVat}>{t('orderscreen.priceWithoutVat', {price: pricingNoVat})}</p>
                        <p className={styles.priceWithVat}>{t('orderscreen.priceWithVat', {price: pricingVat})}</p>
                        
                        <div className={styles.productColumns}>
                            <div className={styles.col0}>
                                {
                                    props.article.pricing.map((price, index) => {
                                        if(index === 0) {
                                            return null;
                                        }
                                        return <span key={index} className={styles.priceSmall}>{t('editscreen.pricingFromNoVat', {amount: price.minAmount, price: getPricingText(i18n.language, props.country, price.pricing)})}</span>
                                    })
                                }
                            </div>
                            <div className={styles.col1}>
                                <span className={styles.productDelivery}>{translate(props.article.deliveryTime, i18n)}</span>
                            </div>
                        </div>
                        {props.article.hasVentilationZipper && <h2>{t('editscreen.incVentilation')}</h2>}
                        {props.article.ventilationArticle && !props.article.hasVentilationZipper && <h2>{t('editscreen.excVentilation')}</h2>}
                        {props.protected && 
                            <div className={`${styles.orderContainer} ${styles.orderContainerProtected}` }>
                                {
                                    props.product.order.map((order, orderIndex) => {
                                        return <div key={orderIndex}>
                                            <div>
                                                <span>{t('orderscreen.amount')}</span>
                                                <div className={styles.protectedText}>
                                                    <p>{order.amount}</p>
                                                </div>
                                            </div>
                                            <div>
                                                <span>{t('orderscreen.size')}</span>
                                                <div className={styles.protectedText}>
                                                    <p>{order.size}</p>
                                                </div>
                                            </div>
                                        </div>
                                    })
                                }
                            </div>
                        }
                    </div>
                </div>
                {!isMobile && <div className={`${styles.productPricing} ${styles.productPricingTop}`}>
                    <p className={styles.priceWithoutVat}>{t('orderscreen.priceWithoutVat', {price: getPricingTextLong(i18n.language, props.country, overallPriceNoVat)})}</p>
                    <p className={styles.priceWithVat}>{t('orderscreen.priceWithVat', {price: getPricingTextLong(i18n.language, props.country, overallPriceVat)})}</p>
                </div>}
                <div className={`${styles.productInfo} ${styles.productInfoRows}`}>
                    {!props.protected && 
                        <div className={styles.orderContainer}>
                            <span>{t('orderscreen.amount')}</span>
                            <span>{t('orderscreen.size')}</span>
                            <span></span>
                            {
                                props.product.order.map((order, orderIndex) => {
                                    return <Fragment key={orderIndex}>
                                        <div className={styles.amount}>
                                            <button onClick={() => {
                                                updatedProduct.order[orderIndex].amount--;
                                                if( updatedProduct.order[orderIndex].amount < 1) {
                                                    updatedProduct.order[orderIndex].amount = 1;
                                                }
                                                if(props.onProductUpdate) {
                                                    props.onProductUpdate(props.index, updatedProduct);
                                                }
                                            }} ><IconMinus/></button>
                                            <input type="number" min={0} max={9999} value={order.amount} onChange={(evt) => {
                                                updatedProduct.order[orderIndex].amount = parseInt(evt.target.value);
                                                if(Number.isNaN(updatedProduct.order[orderIndex].amount)) {
                                                    updatedProduct.order[orderIndex].amount = 1;
                                                }
                                                if( updatedProduct.order[orderIndex].amount < 1) {
                                                    updatedProduct.order[orderIndex].amount = 1;
                                                }
                                                if( updatedProduct.order[orderIndex].amount > 9999) {
                                                    updatedProduct.order[orderIndex].amount = 9999;
                                                }
                                                if(props.onProductUpdate) {
                                                    props.onProductUpdate(props.index, updatedProduct);
                                                }
                                            }}/>
                                            <button onClick={() => {
                                                updatedProduct.order[orderIndex].amount++;
                                                if( updatedProduct.order[orderIndex].amount > 9999) {
                                                    updatedProduct.order[orderIndex].amount = 9999;
                                                }
                                                if(props.onProductUpdate) {
                                                    props.onProductUpdate(props.index, updatedProduct);
                                                }
                                            }} ><IconPlus/></button>
                                        </div>
                                        <div className={styles.size}>
                                            <select value={order.size} onChange={(evt) => {
                                                updatedProduct.order[orderIndex].size = evt.target.value;
                                                if(props.onProductUpdate) {
                                                    props.onProductUpdate(props.index, updatedProduct);
                                                }
                                            }}>
                                                {props.article.sizes.map((size, articleIndex) => {
                                                    return <option key={articleIndex} value={size}>{size}</option>
                                                })}
                                            </select>
                                            <div className={styles.sizeArrow}><IconArrowDown/></div>
                                        </div>
                                        {
                                            (!props.protected && orderIndex === props.product.order.length - 1) && <button data-testid="mymotion_orderListAddSize" className={styles.buttonSecondary} onClick={() => {
                                                updatedProduct.order.push({amount: 1, size: props.article.sizes[0]});
                                                if(props.onProductUpdate) {
                                                    props.onProductUpdate(props.index, updatedProduct);
                                                }
                                            }} ><IconPlus/>{t('orderscreen.addSize')}</button>
                                        }
                                        {
                                            (!props.protected && orderIndex !== props.product.order.length - 1) && <button data-testid="mymotion_orderListRemoveSize" className={styles.buttonSecondary} onClick={() => {
                                                updatedProduct.order = updatedProduct.order.filter((order, index) => {return index !== orderIndex});
                                                if(props.onProductUpdate) {
                                                    props.onProductUpdate(props.index, updatedProduct);
                                                }
                                            }} >{t('orderscreen.removeSize')}</button>
                                        }
                                        {
                                            !props.protected && orderIndex === props.product.order.length - 1 &&
                                            <Fragment>
                                                <div></div>
                                                <div></div>
                                                <button data-testid="mymotion_orderListPersonalize" className={styles.buttonSecondary} onClick={() => {
                                                    if(props.onUiGoTo && props.onUiSetPreviewArticle1 && props.onUiSetEditScreenTab && props.onUiSetDetailArticleIndex) {
                                                        props.onUiGoTo('editscreen');
                                                        props.onUiSetPreviewArticle1(props.article.article);
                                                        props.onUiSetEditScreenTab('logos');
                                                        props.onUiSetDetailArticleIndex(1);
                                                    }
                                                }} ><IconShirt/>{t('editscreen.personalize')}</button>
                                            </Fragment>
                                        }
                                    </Fragment>
                                })
                            }
                        </div>
                    }
                    {
                        getTotalProductOrderAmountForMinimumOrder(props.article, props.config, props.library) < getMinimumOrderAmount(props.article, props.library) &&
                        <p className={styles.minimumOrder}>{t('orderscreen.minimumOrder', {pieces: getMinimumOrderAmount(props.article, props.library)})}</p>
                    }
                </div>
                {!isMobile && <div className={`${styles.productPricing} ${props.product.logos.length > 0 ? styles.productPricingFill : styles.productPricingBottom}`}>
                    {!props.protected && <button data-testid="mymotion_orderListRemoveProduct" className={styles.buttonWhite} onClick={() => {
                            if(props.onProductRemove) {
                                props.onProductRemove(props.index);
                            }
                        }} ><IconTrash /></button>}
                    </div>
                }
                {isMobile && <div className={`${styles.productPricing}`}>
                    <p className={styles.priceWithoutVat}>{t('orderscreen.priceWithoutVat', {price: getPricingTextLong(i18n.language, props.country, overallPriceNoVat)})}</p>
                    <p className={styles.priceWithVat}>{t('orderscreen.priceWithVat', {price: getPricingTextLong(i18n.language, props.country, overallPriceVat)})}</p>
                </div>}
                {
                    props.product.logos.length > 0 && 
                    <Fragment>
                        {!isMobile && 
                            <Fragment>
                                <div className={styles.logoContainer}>
                                    <h2>{t('editscreen.logo.header')}</h2>
                                </div>
                                <div className={styles.productPricingFill}>
                                </div>
                            </Fragment>
                        }
                        {
                            props.product.logos.map((logo, logoIndex) => {
                                let logoPlacementText = props.article.logoPlanes.find((plane) => {
                                    return plane.id === logo.position;
                                }) 
                                let logoFile = props.config.logoFiles.find((file) => {
                                    return file.filename === logo.filename;
                                })
                                let logoArea = props.library.logoAreas.find((area) => {
                                    return area.id === logo.logoAreaId;
                                })

                                if(logoArea === undefined) {
                                    return null;
                                }

                                let logoPrice: PricingData|null = null;
                                for (let index = logoArea.pricing.length - 1; index >= 0; index--) {
                                    const element = logoArea.pricing[index];
                                    if(overallAmount >= element.minAmount) {
                                        logoPrice = element.pricing;
                                        break;
                                    }
                                }
                            
                                if(logoPrice === null) {
                                    return null;
                                }
                            

                                let logoOverallPriceNoVat = multiplyPrice(logoPrice, overallAmount);
                                let logoOverallPriceVat = applyVat(logoOverallPriceNoVat, props.vat);


                                return <Fragment key={logoIndex}>
                                    <div className={styles.logoListEntry}>
                                        {logoFile !== undefined && (logoFile.originalFilename.endsWith('.png') || logoFile.originalFilename.endsWith('.jpg') || logoFile.originalFilename.endsWith('.jpeg')) &&
                                            <div className={styles.logoListEntryImage} style={{backgroundImage: `url(${getURL(GET_LOGO, {
                                                id: props.config.code,
                                                filename: logoFile.filename
                                            })})`}}>
                                            </div>
                                        }
                                        <div className={styles.logoListEntryText}>                                                
                                            <p className={styles.logoListEntryHeader}>{logo.logoType === LogoType.Stitched ? t('editscreen.logo.type.stitch') : t('editscreen.logo.type.print')} | Logo {logoIndex + 1}</p>      
                                            {logoFile !== undefined && 
                                                <Fragment>                                            
                                                    <p>{logoFile.originalFilename}</p>                                                            
                                                    {/*<p className={styles.logoListEntrySize}>{logoFile.size.toFixed(2)} MB</p>*/}
                                                </Fragment>
                                            }
                                            <p>{logoPlacementText !== undefined ? translate(logoPlacementText.name, i18n) : 'NOT FOUND'} ({logo.position})</p>
                                            <p className={styles.logoListEntrySize}>{t('editscreen.logo.area')} {logoArea !== undefined ? `${logoArea.minSize}-${logoArea.maxSize} cm²`: 'NOT FOUND'}</p>
                                        </div>
                                        {props.onProductUpdate !== undefined &&
                                            <button className={styles.logoListRemove} onClick={() => {
                                                let updatedProduct: ProductState = Object.assign({}, props.product);
                                                const logoIndex = updatedProduct.logos.findIndex((logoEntry) => {
                                                    return logoEntry.position === logo.position;
                                                });
                                                if(logoIndex !== -1) {
                                                    updatedProduct.logos.splice(logoIndex, 1);
                                                }
                                                if(props.onProductUpdate !== undefined) {
                                                    props.onProductUpdate(props.index, updatedProduct);
                                                }
                                            }}><IconTrash/></button>
                                        }
                                    </div>
                                    <div className={`${styles.productPricing} ${isMobile ? '' : (logoIndex === 0 ? styles.productPricingFirst : '')} ${isMobile ? '' : (logoIndex === props.product.logos.length - 1 ? styles.productPricingBottom : styles.productPricingFill)}`}>
                                        <p className={styles.priceWithoutVat}>{t('orderscreen.priceWithoutVat', {price: getPricingTextLong(i18n.language, props.country, logoOverallPriceNoVat)})}</p>
                                        <p className={styles.priceWithVat}>{t('orderscreen.priceWithVat', {price: getPricingTextLong(i18n.language, props.country, logoOverallPriceVat)})}</p>
                                    </div>
                                </Fragment>;
                            })
                        }
                    </Fragment>
                }
            </div>
        </div>
    </Fragment>
}
